/* Rebased on the 2026-10-09 current theme export.
   Magnet/pint pricing is unchanged; coaster support is optional and gated. */
(() => {
  async function get() {
    await window.SouvenirMagnets.get();
    const cart = await window.SouvenirPints.get();
    const latest=window.SouvenirCoasters ? await window.SouvenirCoasters.get() : cart;
    return recoverOrdinary(latest);
  }
  const ordinaryJournalKey = () => `souvenir-ordinary-change:${window.SouvenirMagnets.config.productId}`;
  function ordinaryIdentity(line) {
    return JSON.stringify([String(line.product_id),String(line.variant_id ?? line.id),Object.entries(line.properties || {}).sort(([a],[b])=>a.localeCompare(b)),String(line.selling_plan_allocation?.selling_plan?.id || '')]);
  }
  const ordinaryRevision = line => JSON.stringify([ordinaryIdentity(line),line.quantity,line.original_price]);
  async function ordinaryRequest(path,body) {
    const response=await fetch(`${window.SouvenirMagnets.config.root}${path}`,{method:body?'POST':'GET',credentials:'same-origin',cache:'no-store',headers:{Accept:'application/json',...(body?{'Content-Type':'application/json'}:{})},...(body?{body:JSON.stringify(body)}:{})});
    const cart=await response.json();
    if(!response.ok){const error=Error(cart.description || 'Your cart could not be updated.');error.confirmedFailure=response.status>=400&&response.status<500;throw error;}
    return cart;
  }
  function ordinarySettled(cart,saved) {
    const matches=cart.items.filter(line=>ordinaryIdentity(line)===saved.identity);
    // A line-key quantity change cannot recreate an item removed in another window.
    return !matches.length || matches.length===1&&matches[0].quantity===saved.quantity;
  }
  async function recoverOrdinary(cart) {
    if(!localStorage.getItem(ordinaryJournalKey()))return cart;
    return navigator.locks.request('souvenir-cart',async()=>{
      const raw=localStorage.getItem(ordinaryJournalKey());
      if(!raw)return cart;
      const saved=JSON.parse(raw),fresh=await ordinaryRequest('cart.js');
      if(!ordinarySettled(fresh,saved))throw Error('The last item quantity change is not confirmed. Choose “Check cart again” before making another change or checking out.');
      localStorage.removeItem(ordinaryJournalKey());return fresh;
    });
  }
  async function changeOrdinaryLine(key,quantity,expectedRevision) {
    if(typeof key!=='string'||!key||!Number.isSafeInteger(quantity)||quantity<1||quantity>1000||!expectedRevision)throw Error('Choose a whole quantity from 1 to 1000. Use Remove to delete an item.');
    if(!navigator.locks)throw Error('Cart updates are unavailable in this browser. Please try a current browser.');
    await get();
    return navigator.locks.request('souvenir-cart',async()=>{
      const m=window.SouvenirMagnets,p=window.SouvenirPints,c=window.SouvenirCoasters;
      let coasterId=c?.config.productId;
      try{coasterId=coasterId||JSON.parse(document.getElementById('souvenir-coaster-commerce-config')?.textContent||'{}').productId;}catch(_){}
      for(const [family,id] of [['magnet',m.config.productId],['pint',p.config.productId],['coaster',coasterId],['ordinary',m.config.productId]])if(id&&localStorage.getItem(`souvenir-${family}-change:${id}`))throw Error('A cart update is unfinished. Choose “Check cart again” before changing this item.');
      const cart=await ordinaryRequest('cart.js'),line=cart.items.find(item=>item.key===key);
      if(!line||ordinaryRevision(line)!==expectedRevision)throw Error('This item changed in another window. Check your cart again before updating its quantity.');
      if([m.config.productId,p.config.productId,coasterId].some(id=>id!=null&&String(id)===String(line.product_id))||/^(MAG|PINT|COAST)-MIX-/.test(line.sku||''))throw Error('Use this product family’s dedicated controls to change its quantity.');
      if(line.quantity===quantity)return cart;
      const saved={identity:ordinaryIdentity(line),quantity};
      localStorage.setItem(ordinaryJournalKey(),JSON.stringify(saved));
      let error=null;
      try{await ordinaryRequest('cart/change.js',{id:line.key,quantity});}catch(e){error=e;}
      const fresh=await ordinaryRequest('cart.js');
      if(ordinarySettled(fresh,saved)){
        localStorage.removeItem(ordinaryJournalKey());
        window.dispatchEvent(new CustomEvent('souvenir:cart-updated',{detail:fresh}));return fresh;
      }
      if(error?.confirmedFailure){localStorage.removeItem(ordinaryJournalKey());throw error;}
      throw Error('The last item quantity change is not confirmed. Choose “Check cart again”; do not repeat the update yet.');
    });
  }
  window.SouvenirCart = {get,ordinaryRevision,changeOrdinaryLine};
  window.SouvenirCheckout = async () => {
    await get();
    return navigator.locks.request('souvenir-cart',async()=>{
      const m=window.SouvenirMagnets,p=window.SouvenirPints,c=window.SouvenirCoasters;
      let configuredCoasterId=null;
      try { configuredCoasterId=JSON.parse(document.getElementById('souvenir-coaster-commerce-config')?.textContent || '{}').productId; } catch (_) {}
      if(localStorage.getItem(ordinaryJournalKey()))throw Error('An item quantity update is unfinished. Choose “Check cart again” before checkout.');
      // Recheck journals after the final lock: another tab may have changed the cart after get().
      const familyProducts=[['magnet',m.config.productId],['pint',p.config.productId],['coaster',c?.config.productId || configuredCoasterId]];
      for(const [family,productId] of familyProducts) if(productId && localStorage.getItem(`souvenir-${family}-change:${productId}`)) throw Error('A cart update is unfinished. Choose “Check cart again” before checkout.');
      const response=await fetch(`${m.config.root}cart.js`,{cache:'no-store',credentials:'same-origin'});
      if (!response.ok) throw Error('Please check your cart again.');
      const cart=await response.json(),magnets=m.inspect(cart,await m.catalog()),pints=p.inspect(cart,await p.catalog());
      if (!cart.item_count) throw Error('Your cart is empty.');
      if (magnets.invalid.length || pints.invalid.length) throw Error('Review incomplete design selections before checkout.');
      for(const [found,expected] of [[magnets,m.total(magnets.magnets.length)],[pints,p.total(pints.glasses.length,pints.double)]]) {
        if (found.lines.reduce((sum,line)=>sum+line.original_price*line.quantity,0)!==expected) throw Error('Your cart changed. Check prices again before checkout.');
      }
      if (pints.glasses.length && !p.config.checkoutReady) throw Error('Glass checkout is being prepared. Your selection is saved; ordering will open once shipping is ready.');
      // Detect known pack lines independently: a failed/disabled module cannot bypass validation.
      const detectedCoasters=cart.items.filter(line=>(configuredCoasterId && String(line.product_id)===String(configuredCoasterId)) || /^COAST-MIX-/.test(line.sku || ''));
      if (detectedCoasters.length && (!c || typeof c.validateCheckout!=='function')) throw Error('Coaster checkout is not ready. Please check your cart again.');
      if (c) {
        const coasters=await c.validateCheckout(cart),validatedKeys=new Set(coasters.lines.map(line=>line.key));
        if (detectedCoasters.some(line=>!validatedKeys.has(line.key)) || coasters.invalid.length || coasters.lines.reduce((sum,line)=>sum+line.original_price*line.quantity,0)!==coasters.expected) throw Error('Review your coaster packs before checkout.');
      }
      location.assign(`${m.config.root}checkout`);
    });
  };
  window.addEventListener('souvenir:cart-updated',async event=>{
    try {
      const m=window.SouvenirMagnets,p=window.SouvenirPints,c=window.SouvenirCoasters;
      if (!m || !p) return;
      const mf=m.inspect(event.detail,await m.catalog()),pf=p.inspect({items:mf.others},await p.catalog());
      let others=pf.others,coasterCount=0;
      if(c && c.hasLines({items:others})) {
        const cf=c.inspect({items:others},await c.catalog());
        // Keep an honest fallback count if an incomplete line needs attention.
        coasterCount=cf.coasterCount+cf.invalid.reduce((n,entry)=>n+entry.item.quantity,0); others=cf.others;
      }
      const count=mf.magnets.length+pf.glasses.length+coasterCount+others.reduce((n,line)=>n+line.quantity,0);
      document.querySelectorAll('[data-header-cart-count]').forEach(el=>{el.dataset.headerCartCount=count||'';el.classList.toggle('visible',count>0);});
    } catch (_) { /* Cart UI owns visible errors; don't invent a successful count. */ }
  });
})();
